// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for retrieving all accounting periods.
 * Provides type-safe period list retrieval with Zod validation.
 */
import * as z from 'zod';
import { savedAccountingPeriodActivityStatusSchema } from '@/accounting/api/types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';

/**
 * Schema for validating the response from the get accounting periods API endpoint.
 * Includes both the periods data and metadata about the request.
 */
const getAccountingPeriodsResponseSchema = z
  .object({
    data: z.array(savedAccountingPeriodActivityStatusSchema),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, periods: data }));

/**
 * Type definition for the get accounting periods API response.
 * Contains the periods data and request metadata.
 */
type GetAccountingPeriodsResponse = z.infer<
  typeof getAccountingPeriodsResponseSchema
>;

/**
 * Retrieves all accounting periods for the authenticated organization.
 *
 * @returns Promise resolving to the periods data and request metadata
 * @throws Will throw an error if the API request fails or response validation fails
 */
export async function getAccountingPeriods(): Promise<GetAccountingPeriodsResponse> {
  const url = '/v1/accounting/periods';
  const response = await api.get(url);

  return parseResponse(getAccountingPeriodsResponseSchema, response.data, {
    schemaName: 'AccountingPeriods',
    endpoint: url,
  });
}
